import { ToggleSwitch, Typography } from "@foodpilot/foods";
import { Box, FormControlLabel, Stack, Switch } from "@mui/material";
import { Controller, useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { usePackagingReference } from "src/api/sure-api/usePackagingReference";
import { useUpsertPackagingElement } from "src/api/client-api/packagings/useUpsertPackagingElement";
import { toPackagingShareWrites } from "src/api/client-api/packagings/useUpsertPackaging";
import { useCalculatePackagingPreview } from "src/api/client-api/packagings/useCalculatePackagingPreview";
import type { Packaging } from "src/api/client-api/packagings/useGetOnePackaging";
import { Loading } from "src/components/utils/Loading";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { elementSchema, type ElementFormData } from "./elementSchema";
import { resolveWeightOnReferenceChange } from "./resolveWeightOnReferenceChange";
import { FormCardSection } from "../_shell/FormCardSection";
import { PackagingFormLayout } from "../_shell/PackagingFormLayout";
import { RhfFoodsSelect, RhfFoodsTextField } from "../_shell/RhfFoodsFields";
import { AssociatedOrganizationsField } from "./AssociatedOrganizationsField";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";
import { EntityValidationBanner } from "src/components/EntityForm/EntityValidationBanner";
import { EntityUnsavedChangesDialog } from "src/components/EntityForm/EntityUnsavedChangesDialog";
import { useEntityFormLeaveGuard } from "src/components/EntityForm/useEntityFormLeaveGuard";

type Props = {
    existing?: Packaging;
};

type PackagingReference = { id: number; label: string; defaultWeight: number | null };

export const CreatePackagingElement = ({ existing }: Props = {}) => {
    const { t } = useTranslation();
    const { currentCampaign, selectedCompany } = useFoodpilotContext();
    const { packagingReferences, isLoading } = usePackagingReference();
    const upsert = useUpsertPackagingElement();
    const isProductUxV2 = useIsProductUxV2();
    const appearance = isProductUxV2 ? "product-v2" : "legacy";
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [validationAttempt, setValidationAttempt] = useState(0);
    const validationBannerRef = useRef<HTMLDivElement>(null);
    const saveForExitRef = useRef(false);
    const submitInFlightRef = useRef(false);

    const isEdit = existing !== undefined;
    const sortedReferences = useMemo<PackagingReference[]>(
        () => packagingReferences.toSorted((a, b) => a.label.localeCompare(b.label)),
        [packagingReferences],
    );

    const defaultValues: ElementFormData = useMemo(
        () => ({
            name: existing?.name ?? "",
            externalId: existing?.externalId ?? "",
            packagingReferenceId: existing?.packagingReferenceId ?? 0,
            weight: existing?.weight ?? 0,
            recycled: existing?.recycled ?? false,
            recycledProportion: existing?.recycledProportion ?? null,
            recyclable: existing?.recyclable ?? null,
            reuse: existing?.reuse ?? false,
            shares: existing?.shares?.map((share) => share.organization) ?? [],
        }),
        [existing],
    );

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, dirtyFields, isDirty, isSubmitted },
    } = useForm<ElementFormData>({
        resolver: zodResolver(elementSchema),
        mode: "onSubmit",
        reValidateMode: "onChange",
        defaultValues,
    });
    useEffect(() => {
        if (validationAttempt === 0) return;
        validationBannerRef.current?.focus({ preventScroll: true });
        validationBannerRef.current?.scrollIntoView?.({ block: "start" });
    }, [validationAttempt]);
    const watchedValues = watch();
    const listUrl = URLHandler.packaging.list(selectedCompany?.id ?? 0);
    const leaveGuard = useEntityFormLeaveGuard({ dirty: isDirty, busy: upsert.isLoading });
    const requiredFieldMessage = t("form.required_field", "This field is required.");
    const resolveErrorMessage = (message: string | undefined) =>
        message === "form.required_field" ? requiredFieldMessage : message;
    const recycledProportionError = errors.recycledProportion?.message;
    const recycledProportionErrorMessage =
        recycledProportionError === "packaging_element.recycled_proportion_must_be_null_when_not_recycled" ?
            t(
                "packaging_element.recycled_proportion_must_be_null_when_not_recycled",
                "Clear the recycled percentage when the material contains no recycled content.",
            )
        : recycledProportionError === "packaging_element.recycled_proportion_required" ?
            t("packaging_element.recycled_proportion_required", "Enter the percentage of recycled material.")
        :   recycledProportionError;
    const preview = useCalculatePackagingPreview(
        {
            type: "element",
            packagingReferenceId: watchedValues.packagingReferenceId || null,
            weight: watchedValues.weight,
            recycled: watchedValues.recycled,
            recycledProportion: watchedValues.recycledProportion,
            recyclable: watchedValues.recyclable,
            reuse: watchedValues.reuse,
        },
        {
            enabled: watchedValues.packagingReferenceId > 0 && watchedValues.weight > 0,
        },
    );

    if (selectedCompany === null || currentCampaign === null) {
        return null;
    }

    if (isLoading) {
        return <Loading appearance={appearance} />;
    }

    const onCancel = () => leaveGuard.requestExit(listUrl);

    const onSubmit = (data: ElementFormData) => {
        if (submitInFlightRef.current) return;
        submitInFlightRef.current = true;
        setSubmitError(null);
        upsert.mutate(
            {
                organizationId: selectedCompany.id,
                campaignId: currentCampaign.id,
                packaging: {
                    ...(isEdit ? { id: existing.id } : {}),
                    name: data.name,
                    externalId: data.externalId || null,
                    packagingReferenceId: data.packagingReferenceId,
                    weight: data.weight,
                    recycled: data.recycled,
                    recycledProportion: data.recycledProportion,
                    recyclable: data.recyclable,
                    reuse: data.reuse,
                    shares: toPackagingShareWrites(data.shares ?? []),
                },
            },
            {
                onSuccess: () => {
                    saveForExitRef.current = false;
                    leaveGuard.completeSave(listUrl);
                },
                onError: (err: unknown) => {
                    setSubmitError(err instanceof Error ? err.message : String(err));
                    if (saveForExitRef.current) {
                        saveForExitRef.current = false;
                        leaveGuard.cancelSave();
                    }
                },
                onSettled: () => {
                    submitInFlightRef.current = false;
                },
            },
        );
    };

    const submit = handleSubmit(onSubmit, () => {
        setValidationAttempt((current) => current + 1);
        if (saveForExitRef.current) {
            saveForExitRef.current = false;
            leaveGuard.cancelSave();
        }
    });

    const handleReferenceChange = (defaultWeight: number | null) => {
        const nextWeight = resolveWeightOnReferenceChange(defaultWeight, Boolean(dirtyFields.weight));
        if (nextWeight !== null) {
            setValue("weight", nextWeight, { shouldValidate: isSubmitted });
        }
    };

    return (
        <>
            <PackagingFormLayout
                appearance={appearance}
                title={
                    isEdit ?
                        t("packaging_element.edit.title", "Edit packaging component")
                    :   t("packaging_element.create.title", "New packaging component")
                }
                activeBreadcrumb={
                    isEdit ?
                        t("packaging_element.edit.breadcrumb", "Edit packaging component")
                    :   t("packaging_element.create.breadcrumb", "New packaging component")
                }
                companyName={selectedCompany.name}
                onCancel={onCancel}
                onCancelLabel={t("cancel", "Cancel")}
                onSaveLabel={t("save", "Save")}
                onSubmit={() => void submit()}
                isSaving={upsert.isLoading}
                packaging={existing}
                previewScores={preview.data}
                isPreviewLoading={preview.isFetching}
            >
                <EntityValidationBanner
                    bannerRef={validationBannerRef}
                    entityLabel={t("entity_form.entity.packaging_component", "packaging component")}
                    issueCount={Object.keys(errors).length}
                />
                <FormCardSection
                    title={t("packaging_element.identity.title", "1/3 • Identity")}
                    description={t(
                        "packaging_element.identity.description",
                        "Information needed to identify your packaging component.",
                    )}
                    bodyGap={24}
                >
                    <Stack direction={{ xs: "column", sm: "row" }} gap={3}>
                        <Box flex={1} minWidth={0}>
                            <RhfFoodsTextField
                                control={control}
                                name="name"
                                label={t("packagings.name", "Packaging name")}
                                required
                                errorMessage={resolveErrorMessage(errors.name?.message)}
                            />
                        </Box>
                        <Box flex={1} minWidth={0}>
                            <RhfFoodsTextField
                                control={control}
                                name="externalId"
                                label={t("unique_identifier", "Unique identifier")}
                                helperText={t(
                                    "packagings.external_id.help",
                                    "Optional internal reference used by your organisation.",
                                )}
                            />
                        </Box>
                    </Stack>
                    <Controller
                        name="shares"
                        control={control}
                        render={({ field }) => (
                            <AssociatedOrganizationsField
                                appearance={appearance}
                                value={field.value ?? []}
                                onChange={field.onChange}
                            />
                        )}
                    />
                </FormCardSection>

                <FormCardSection title={t("packaging_element.material.title", "2/3 • Material")} bodyGap={16}>
                    <Stack direction={{ xs: "column", sm: "row" }} gap={2} alignItems="flex-end">
                        <Box flex={1} minWidth={0}>
                            <RhfFoodsSelect<ElementFormData, "packagingReferenceId", PackagingReference>
                                appearance={appearance}
                                control={control}
                                name="packagingReferenceId"
                                options={sortedReferences}
                                getId={(ref) => ref.id}
                                getName={(ref) => ref.label}
                                label={t("packaging_element.material_type", "Type of material")}
                                placeholder={t("packaging_element.reference_placeholder", "Select a reference")}
                                searchPlaceholder={t(
                                    "packaging_element.reference_search_placeholder",
                                    "Search a reference...",
                                )}
                                emptyOptionsText={t("packaging_element.reference_empty", "No reference available")}
                                noResultText={t("packaging_element.reference_no_result", "No reference found")}
                                errorMessage={resolveErrorMessage(errors.packagingReferenceId?.message)}
                                onAfterChange={(value) => handleReferenceChange(value?.defaultWeight ?? null)}
                                required
                            />
                        </Box>
                        <Box sx={{ width: { xs: "100%", sm: 180 } }}>
                            <RhfFoodsTextField
                                control={control}
                                name="weight"
                                label={t("packaging_element.weight", "Unit weight")}
                                required
                                type="number"
                                min={0}
                                step={0.001}
                                unit={t("weight.grams", "g")}
                                errorMessage={resolveErrorMessage(errors.weight?.message)}
                            />
                        </Box>
                    </Stack>
                </FormCardSection>

                <FormCardSection
                    title={t("packaging_element.environmental.title", "3/3 • Environmental properties")}
                    bodyGap={16}
                >
                    <RhfFoodsTextField
                        control={control}
                        name="recycledProportion"
                        label={t(
                            "packaging_element.recycled_proportion",
                            "Percentage of recycled raw material in this material",
                        )}
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        unit="%"
                        errorMessage={recycledProportionErrorMessage}
                        onBeforeChange={(next) => {
                            setValue("recycled", next !== null, { shouldValidate: false });
                        }}
                    />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <Controller
                            name="recyclable"
                            control={control}
                            render={({ field }) => {
                                const label = t("packaging_element.recyclable", "Recyclable material");

                                return isProductUxV2 ?
                                        <ToggleSwitch
                                            checked={Boolean(field.value)}
                                            withStateIcons
                                            label={label}
                                            inputProps={{ "aria-label": label, role: "switch" }}
                                            onChange={(event) => field.onChange(event.target.checked)}
                                        />
                                    :   <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={Boolean(field.value)}
                                                    onChange={(event) => field.onChange(event.target.checked)}
                                                />
                                            }
                                            label={label}
                                        />;
                            }}
                        />
                        <Controller
                            name="reuse"
                            control={control}
                            render={({ field }) => {
                                const label = t("packaging_element.reuse", "Reusable material");

                                return isProductUxV2 ?
                                        <ToggleSwitch
                                            checked={field.value}
                                            withStateIcons
                                            label={label}
                                            inputProps={{ "aria-label": label, role: "switch" }}
                                            onChange={(event) => field.onChange(event.target.checked)}
                                        />
                                    :   <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={field.value}
                                                    onChange={(event) => field.onChange(event.target.checked)}
                                                />
                                            }
                                            label={label}
                                        />;
                            }}
                        />
                    </Stack>
                    {submitError && (
                        <Typography color="error" variant="body2">
                            {submitError}
                        </Typography>
                    )}
                </FormCardSection>
            </PackagingFormLayout>
            <EntityUnsavedChangesDialog
                open={leaveGuard.open}
                busy={upsert.isLoading}
                onSave={() => {
                    saveForExitRef.current = true;
                    leaveGuard.beginSave();
                    void submit();
                }}
                onDiscard={() => {
                    saveForExitRef.current = false;
                    leaveGuard.discard();
                }}
                onKeepEditing={leaveGuard.keepEditing}
            />
        </>
    );
};
